import React from 'react';
import {
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Button,
    Heading,
    Text,
} from 'components/v4';
import { Box, GridItem, Grid, Stack } from '@chakra-ui/react';
import useGetCompanyApproval from '../queries/useGetCompanyApproval';
import useApproveCompany from '../queries/useApproveCompany';

const useApproval = (company) => {
    const fields = [
        { placeholder: 'Legal Entity Name', value: company?.entityName },
        { placeholder: 'Friendly Company Name', value: company?.name },
    ];

    return {
        denyPrompt: 'This action cannot be undone.',
        companyInfoHeader: 'Company Information',
        requiredForApprovalHeader: 'Required For Approval',
        fields,
        requiredForApprovalPlaceholder: 'Default Plan',
        requiredForApprovalValue: company?.defaultPlan || 'No default plan set',
        requiredForApprovalBorderColor: company?.defaultPlan
            ? '#5bc0de'
            : 'red',
    };
};

const CompanyApprovalModal = ({ isOpen, onClose, company, onApproved }) => {
    const { data: approvalResp, isLoading: approvalLoading } =
        useGetCompanyApproval(
            isOpen && company?.id ? company.id : null,
            isOpen,
        );
    const approveMutation = useApproveCompany(company?.id);

    const {
        companyInfoHeader,
        requiredForApprovalHeader,
        fields,
        requiredForApprovalBorderColor,
        requiredForApprovalPlaceholder,
        requiredForApprovalValue,
    } = useApproval(approvalResp || company);

    const handleApprove = async (asTrial = false) => {
        if (!approvalResp?.canApprove) {
            alert('This company cannot be approved at this time.');
            return;
        }
        try {
            await approveMutation.mutateAsync({
                approvalData: approvalResp,
                asTrial,
            });
            if (onApproved) onApproved();
            onClose();
        } catch (e) {
            alert('Approval failed. Please try again.');
        }
    };

    if (!company) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent minW="40%">
                <ModalHeader>
                    Approval Company : {approvalResp?.name || company.name}
                </ModalHeader>
                <ModalBody>
                    <Stack spacing={4}>
                        <Heading as="h4" size="md">
                            {companyInfoHeader}
                        </Heading>
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                            {fields.map(({ placeholder, value }) => (
                                <React.Fragment key={placeholder}>
                                    <GridItem>
                                        <Text fontWeight="bold">
                                            {placeholder}
                                        </Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text>{value}</Text>
                                    </GridItem>
                                </React.Fragment>
                            ))}
                        </Grid>
                        <Heading as="h4" size="md">
                            {requiredForApprovalHeader}
                        </Heading>
                        <Box
                            borderRadius="5px"
                            border={`2px solid ${requiredForApprovalBorderColor}`}
                            pl="10px"
                            mt="10px"
                            display="flex"
                            justifyContent="left">
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignSelf="center"
                                mt="10px"
                                mb="10px"
                                width="100%">
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    gap={10}
                                    alignItems="center">
                                    <Text fontWeight="bold">
                                        {requiredForApprovalPlaceholder}
                                    </Text>
                                    <Text>{requiredForApprovalValue}</Text>
                                </Box>
                            </Box>
                        </Box>
                    </Stack>
                </ModalBody>
                <ModalFooter justifyContent="center">
                    <Button
                        mr={3}
                        onClick={onClose}
                        isDisabled={
                            approvalLoading || approveMutation.isLoading
                        }
                        variant="outline">
                        Close
                    </Button>
                    <Button
                        mr={3}
                        onClick={() => handleApprove(false)}
                        isLoading={approveMutation.isLoading}
                        isDisabled={
                            !approvalResp?.canApprove || approvalLoading
                        }>
                        Approve
                    </Button>
                    <Button
                        onClick={() => handleApprove(true)}
                        isLoading={approveMutation.isLoading}
                        isDisabled={
                            !approvalResp?.canApprove || approvalLoading
                        }>
                        Approve As Trial
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CompanyApprovalModal;
